/**
 * Account-security form contracts — TW-3465 (settings → Security: in-app password change).
 *
 * The actual change runs through Better Auth's `change-password` endpoint, which verifies the
 * *current* password server-side and re-hashes the new one. So what lives here is only the
 * framework-free *shape* the web form and any server-side guard share: that all three fields are
 * present, the new password clears a minimum length, and the confirmation matches. Current-password
 * verification is Better Auth's job, not this schema's; the live strength meter (`passwordStrength`)
 * stays a web-only UI affordance.
 *
 * Pure + deterministic, like the MFA helpers next door — unit-testable with no I/O.
 */
import { z } from "zod";

/**
 * Minimum new-password length. Mirrors Better Auth's default `minPasswordLength` (8), so the form
 * blocks a too-short password before the round-trip rather than surfacing a server error.
 */
export const MIN_PASSWORD_LENGTH = 8;

/**
 * The password-change form: current password (any non-empty value — Better Auth checks it),
 * a new password of at least MIN_PASSWORD_LENGTH, and a confirmation that must match the new one.
 * The match failure is attached to `confirmPassword` so the form can pin the error to that field.
 */
export const changePasswordSchema = z
  .object({
    currentPassword: z.string().min(1, "Enter your current password"),
    newPassword: z.string().min(MIN_PASSWORD_LENGTH, `Use at least ${MIN_PASSWORD_LENGTH} characters`),
    confirmPassword: z.string().min(1, "Re-enter your new password"),
  })
  .refine((v) => v.newPassword === v.confirmPassword, {
    message: "The new passwords don't match",
    path: ["confirmPassword"],
  });
export type ChangePasswordInput = z.infer<typeof changePasswordSchema>;
